import React from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DashboardIcon from "@mui/icons-material/Dashboard";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SettingsIcon from "@mui/icons-material/Settings";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import GroupIcon from "@mui/icons-material/Group";

import {
  DRAWER_OPEN_WIDTH,
  DRAWER_CLOSE_WIDTH,
  APPBAR_HEIGHT,
} from "./constants";

import { useProjects } from "../context/ProjectContext";

const SideDrawer = ({ open, mobileOpen, isMobile, onClose, navigate, isActive }) => {
  const { selectedProjectId } = useProjects();

  const menuItems = [
    {
      label: "Home",
      icon: <HomeIcon />,
      path: "/homepage",
      action: () => navigate("/homepage"),
    },
    {
      label: "Schedule",
      icon: <ScheduleIcon />,
      path: "/schedule",
      action: () => {
        if (selectedProjectId) {
          navigate(`/schedule/${selectedProjectId}`);
        }
      },
    },
    {
      label: "Issue Management",
      icon: <WarningAmberIcon />,
      path: "/issues",
      action: () => {
        if (selectedProjectId) {
          navigate(`/issues/${selectedProjectId}`);
        }
      },
    },
    {
      label: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard",
      action: () => {
        if (selectedProjectId) {
          navigate(`/dashboard/${selectedProjectId}`);
        }
      },
    },
    {
      label: "Files",
      icon: <FileIcon />,
      path: "/files",
      action: () => {
        if (selectedProjectId) {
          navigate("/files");
        }
      },
    },
    {
      label: "Members",
      icon: <GroupIcon />,
      path: "/members",
      action: () => {
        if (selectedProjectId) {
          navigate("/members");
        }
      },
    },
    {
      label: "Reference QR",
      icon: <QrCodeScannerIcon />,
      path: "/reference-qr",
      action: () => {
        if (selectedProjectId) {
          navigate(`/reference-qr/${selectedProjectId}`);
        }
      },
    },
  ];

  const drawerStyles = {
    width: isMobile ? 280 : open ? DRAWER_OPEN_WIDTH : DRAWER_CLOSE_WIDTH,
    flexShrink: 0,

    "& .MuiDrawer-paper": {
      width: isMobile ? 280 : open ? DRAWER_OPEN_WIDTH : DRAWER_CLOSE_WIDTH,
      transition: "width 0.2s ease",
      overflowX: "hidden",
      boxSizing: "border-box",
      borderRight: "1px solid var(--border, #E5E7EB)",
      backgroundColor: "var(--surface-subtle, #F9FAFB)",
      mt: `${APPBAR_HEIGHT}px`,
      height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
      px: 1,
      py: 2,
    },
  };

  const menuItemStyles = (active) => ({
    mb: 0.5,
    borderRadius: "10px",
    minHeight: 48,
    px: 1.5,
    backgroundColor: active ? "var(--nav-active, #E8F5C8)" : "transparent",

    "&:hover": {
      backgroundColor: active ? "var(--nav-active, #E8F5C8)" : "var(--nav-hover, #F3F4F6)",
    },
  });

  const iconStyles = (active) => ({
    color: active ? "#9AE600" : "#9CA3AF",
  });

  const textStyles = (active) => ({
    fontSize: 14,
    fontWeight: 500,
    color: active ? "var(--text-primary, #111827)" : "var(--text-muted, #6B7280)",
  });

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={drawerStyles}
    >
      <List disablePadding>


        {/* Main Menu */}
        {menuItems.map((item) => {
          const active = isActive(item.path);

          return (
            <ListItemButton
              key={item.label}
              onClick={() => {
                item.action();
                if (isMobile) onClose();
              }}
              sx={menuItemStyles(active)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {React.cloneElement(item.icon, {
                  sx: iconStyles(active),
                })}
              </ListItemIcon>

              {open && (
                <ListItemText
                  primary={
                    <Typography sx={textStyles(active)}>
                      {item.label}
                    </Typography>
                  }
                />
              )}
            </ListItemButton>
          );
        })}

        <Box sx={{ flexGrow: 1, minHeight: 20 }} />

        <Divider sx={{ my: 2 }} />

        {/* Settings */}
        <ListItemButton
          onClick={() => {
            if (selectedProjectId) {
              navigate(`/settings/${selectedProjectId}`);
            }
            if (isMobile) onClose();
          }}
          sx={menuItemStyles(isActive("/settings"))}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SettingsIcon sx={iconStyles(isActive("/settings"))} />
          </ListItemIcon>

          {open && (
            <ListItemText
              primary={
                <Typography sx={textStyles(isActive("/settings"))}>
                  Settings
                </Typography>
              }
            />
          )}
        </ListItemButton>
      </List>
    </Drawer>
  );
};

export default SideDrawer;